// Core
import { Spinner } from '@components/Spinner'
// Components
import * as Popover from '@radix-ui/react-popover'
import _ from 'lodash'
import React, {
  type ChangeEvent,
  type ComponentProps,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
// Icons
import { PiMagnifyingGlass, PiX } from 'react-icons/pi'
// Libs
import { twMerge } from 'tailwind-merge'
import { tv, type VariantProps } from 'tailwind-variants'

const comboboxVariants = tv({
  base: 'absolute text-base duration-300 text-text-primary group-data-[disabled=false]:group-hover:text-text-primary group-enabled:group-hover:cursor-pointer group-data-[active=true]:text-text-primary   group-data-[error=true]:group-data-[active=true]:text-error group-data-[disabled=true]:text-borderColor/30',
  variants: {
    variant: {
      form: 'group-data-[active=true]:mb-[4.875rem] group-data-[active=true]:ml-[-0.75rem] group-data-[active=true]:text-sm',
      filter:
        'block px-1 bg-white group-data-[active=true]:mb-[2.5rem] group-data-[active=true]:ml-[-0.25rem] group-data-[active=true]:text-xs',
    },
  },
  defaultVariants: {
    variant: 'filter',
  },
})

type ComboboxVariantions = VariantProps<typeof comboboxVariants>
type ComboboxProps = ComboboxVariantions &
  ComponentProps<'input'> & {
    items: any[]
    originalItems?: any[]
    label: string
    className?: string
    labelProperty?: string
    valueProperty?: string
    errorMessage?: string | null
    onValueChange: (value: string) => void
    nativeOnChange?: (event: ChangeEvent<HTMLInputElement>) => void
    loading?: boolean
    Prefix?: JSX.Element
    searchKeys?: string[]
    maxHeight?: string // Nova prop para controlar o max-height
  }

/**
 *
 * This component is a combination of an input and a select, it is used when you want to give the user the option to search for an item in a list of items
 * @param items - List of items that will be displayed in the select
 * @param label - Label that will be displayed above the input
 * @param labelProperty - Property of the item that will be displayed in the select, by default it is 'label'
 * @param valueProperty - Property of the item that will be returned when the user selects an item, by default it is 'value'
 * @param maxHeight - Optional maximum height of the dropdown content (defaults to '10rem')
 */
export function Combobox({
  items,
  originalItems,
  label,
  variant,
  className,
  onValueChange,
  loading = false,
  errorMessage = null,
  labelProperty = 'label',
  valueProperty = 'value',
  maxHeight = '10rem', // Valor padrão fixo como '10rem'
  ...props
}: ComboboxProps): JSX.Element {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [search, setSearch] = useState<string>(
    props.value ? getItemByValue(props.value) : '',
  )
  const [isOpen, setIsOpen] = useState(false)
  const customId = useId()

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  function getItemByValue(value: any): string {
    const item = items?.find(
      item => String(item[valueProperty]) === String(value),
    )
    return item ? item[labelProperty] : ''
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setIsOpen(true)
    if (props.nativeOnChange) {
      props.nativeOnChange(e)
    }
    const newSearch = e.target.value
    setSearch(newSearch)
  }

  function handleClearSearch(): void {
    setSearch('')
    onValueChange('')
  }

  function handleComboboxBlur(): void {
    setIsOpen(false)
  }

  function handleSelectItem(item: any): void {
    setSearch(item[labelProperty])
    handleComboboxBlur()
    onValueChange(item[valueProperty])
  }

  useEffect(() => {
    setSearch(props.value ? getItemByValue(props.value) : '')
  }, [props.value])

  const hasError = !!errorMessage
  const isDisabled = props.disabled
  const placeholder = search ? '' : props.placeholder

  function findExactMatch(iataProperty?: string): number {
    if (!iataProperty || !search) {
      return -1
    }
    const upperCaseSearch = search.toUpperCase()
    for (let i = 0; i < items.length; i++) {
      if (_.upperCase(items[i][iataProperty]) === upperCaseSearch) {
        return i
      }
    }
    return -1
  }

  function filteredItems(): any[] {
    let filteredList = [...(originalItems ?? items)]
    const exactMatchIndex = findExactMatch('iata')
    if (exactMatchIndex !== -1) {
      const exactMatch = filteredList.splice(exactMatchIndex, 1)[0]
      filteredList.unshift(exactMatch)
    }
    filteredList = filteredList.filter(item => {
      return item[labelProperty].toLowerCase().includes(search.toLowerCase())
    })
    return filteredList
  }

  return (
    <Popover.Root onOpenChange={setIsOpen} open={isOpen}>
      <div className={twMerge('w-full space-y-1.5 relative', className)}>
        <div className="flex flex-col w-full relative">
          <div
            data-active={!!search || isOpen}
            data-error={hasError}
            data-disabled={isDisabled}
            className="
            group mt-auto p-3 h-[2.6rem] w-full flex items-center justify-start border border-border-inputDefault rounded-[0.375rem] transition-all
            enabled:hover:cursor-pointer
            data-[disabled=true]:border-borderColor/30
            data-[error=true]:border-error"
          >
            <label className={comboboxVariants({ variant })}>
              {_.upperFirst(label)}
            </label>
            <Popover.Trigger
              disabled={isDisabled}
              className="z-0 w-full flex items-center justify-between gap-4"
            >
              <input
                {...props}
                ref={inputRef}
                className="
                relative
                h-full w-full z-10 capitalize border-none bg-transparent leading-6 text-text-primary
                focus:outline-none
                group-data-[disabled=true]:text-borderColor/30"
                id={props.id ?? customId}
                onChange={handleInputChange}
                placeholder={isOpen ? _.upperFirst(placeholder) : ''}
                value={search.toLowerCase()}
              />
              {search ? (
                <button
                  disabled={loading}
                  type="button"
                  onClick={handleClearSearch}
                >
                  {loading ? (
                    <Spinner />
                  ) : (
                    <PiX
                      color={isDisabled ? 'rgba(140,140,140, 0.3)' : '#24262B'}
                      className="group-data-[disabled=true]:hidden"
                    />
                  )}
                </button>
              ) : (
                <label htmlFor={props.id ?? customId}>
                  {loading ? (
                    <Spinner />
                  ) : (
                    <PiMagnifyingGlass
                      color={isDisabled ? 'rgba(140,140,140, 0.3)' : '#24262B'}
                    />
                  )}
                </label>
              )}
            </Popover.Trigger>
          </div>
        </div>
        {hasError && (
          <span className="block text-error text-sm">{errorMessage}</span>
        )}
        <Popover.Content
          sticky="always"
          side="bottom"
          sideOffset={21}
          className={twMerge(`
            flex flex-col items-start
            w-[calc(var(--radix-popover-trigger-width)+24px)]
            bg-white p-2 border border-text-primary z-[999] overflow-auto rounded-[0.375rem]
            scrollbar-thin scrollbar-thumb-primary scrollbar-thumb-rounded pr-4
          `)}
          style={{ maxHeight }} // Aplicando maxHeight via estilo inline
        >
          {filteredItems()?.length > 0 ? (
            filteredItems()
              ?.map((item, index) => (
                <Item
                  Prefix={props.Prefix}
                  key={index}
                  text={item[labelProperty]}
                  value={item[valueProperty]}
                  onClick={() => {
                    handleSelectItem(item)
                  }}
                />
              ))
              .slice(0, 999)
          ) : (
            <span className="text-text-primary block p-2">
              {t('noResultsFoundForYourSearch')}
            </span>
          )}
        </Popover.Content>
      </div>
    </Popover.Root>
  )
}

type ItemProps = ComponentProps<'button'> & {
  text: string
  Prefix?: JSX.Element
}

export function Item({ text, ...props }: ItemProps): JSX.Element {
  return (
    <button
      className="outline-none text-text-primary duration-300 hover:border-none hover:outline-none p-2 pr-4 cursor-pointer hover:bg-zinc-50 w-full flex items-start justify-between"
      {...props}
    >
      {props.Prefix ? (
        <div className="flex-1 flex items-center justify-start">
          {props.Prefix}
          <span className="ml-2 text-text-primary text-left capitalize">
            {text.toLowerCase()}
          </span>
        </div>
      ) : (
        <span className="text-text-primary text-left capitalize">
          {text.toLowerCase()}
        </span>
      )}
    </button>
  )
}
